<div class="p-6 space-y-8 p-container">
  <div *ngIf="dietData">
    <!-- Page Header -->
    <div>
      <h1 class="text-2xl font-bold text-foreground">Diet Summary</h1>
      <p class="text-foreground">Personalized nutrition and meal plan</p>
    </div>

    <div class="mt-6">
      <p class="text-gray-500 mb-4 text-sm border-l-4 border-primary pl-4 py-2 bg-amber-50">
        {{
          dietData?.diet_summary?.issue_summary || 'No description available for this diet summary.'
        }}
      </p>
    </div>

    <!-- ============================= -->
    <!-- Nutrition Summary Cards -->
    <!-- ============================= -->

    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-6">
      <!-- Calories -->
      <div
        class="rounded-2xl p-6 bg-gradient-to-br from-red-50 to-red-100 border border-red-200 hover:shadow-xl hover:-translate-y-1 transition duration-300"
      >
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-gray-700 font-semibold">Calories</h3>

          <div
            class="w-10 h-10 flex items-center justify-center rounded-full bg-red-400 text-white"
          >
            <i class="pi pi-sparkles"></i>
          </div>
        </div>

        <p-progressBar
          styleClass="custom-progress-red"
          [value]="dietData?.diet_plan?.nutrition_summary?.calories / 20"
        >
        </p-progressBar>

        <div class="mt-3 text-2xl font-bold text-red-600">
          {{ dietData?.diet_plan?.nutrition_summary?.calories }} kcal
        </div>
      </div>

      <!-- Protein -->
      <div
        class="rounded-2xl p-6 bg-gradient-to-br from-green-50 to-green-100 border border-green-200 hover:shadow-xl hover:-translate-y-1 transition duration-300"
      >
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-gray-700 font-semibold">Protein</h3>

          <div
            class="w-10 h-10 flex items-center justify-center rounded-full bg-green-500 text-white"
          >
            <i class="pi pi-heart"></i>
          </div>
        </div>

        <p-progressBar
          styleClass="custom-progress-green"
          [value]="dietData?.diet_plan?.nutrition_summary?.protein_g"
        >
        </p-progressBar>

        <div class="mt-3 text-2xl font-bold text-green-600">
          {{ dietData?.diet_plan?.nutrition_summary?.protein_g }} g
        </div>
      </div>

      <!-- Carbs -->
      <div
        class="rounded-2xl p-6 bg-gradient-to-br from-yellow-50 to-yellow-100 border border-yellow-200 hover:shadow-xl hover:-translate-y-1 transition duration-300"
      >
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-gray-700 font-semibold">Carbs</h3>

          <div
            class="w-10 h-10 flex items-center justify-center rounded-full bg-yellow-500 text-white"
          >
            <i class="pi pi-bolt"></i>
          </div>
        </div>

        <p-progressBar
          styleClass="custom-progress-yellow"
          [value]="dietData?.diet_plan?.nutrition_summary?.carbs_g"
        >
        </p-progressBar>

        <div class="mt-3 text-2xl font-bold text-yellow-600">
          {{ dietData?.diet_plan?.nutrition_summary?.carbs_g }} g
        </div>
      </div>

      <!-- Fats -->
      <div
        class="rounded-2xl p-6 bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200 hover:shadow-xl hover:-translate-y-1 transition duration-300"
      >
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-gray-700 font-semibold">Fats</h3>

          <div
            class="w-10 h-10 flex items-center justify-center rounded-full bg-purple-500 text-white"
          >
            <i class="pi pi-chart-pie"></i>
          </div>
        </div>

        <p-progressBar
          styleClass="custom-progress-purple"
          [value]="dietData?.diet_plan?.nutrition_summary?.fats_g"
        >
        </p-progressBar>

        <div class="mt-3 text-2xl font-bold text-purple-600">
          {{ dietData?.diet_plan?.nutrition_summary?.fats_g }} g
        </div>
      </div>
    </div>

    <!-- focus area  -->

    <div class="rounded-2xl border border-gray-200 bg-card shadow-md p-6 space-y-6 mt-3">
      <!-- Header -->
      <div class="flex items-center gap-3">
        <div
          class="w-10 h-10 flex items-center justify-center rounded-full bg-green-500 text-white"
        >
          <i class="pi pi-star"></i>
        </div>

        <h2 class="text-2xl font-bold text-foreground">Diet Focus Areas</h2>
      </div>

      <!-- Focus List -->
      <div class="grid md:grid-cols-2 gap-4">
        <div
          *ngFor="let focus of dietData?.diet_summary?.diet_focus; let i = index"
          class="gap-4 rounded-xl border border-green-100 bg-green-50 items-center flex p-4 hover:shadow-md transition"
        >
          <!-- Text -->
          <p class="text-gray-700 text-sm leading-relaxed">
            {{ focus }}
          </p>
        </div>
      </div>
    </div>

    <!-- ============================= -->
    <!-- Nutrition Chart -->
    <!-- ============================= -->

    <div class="rounded-2xl border border-gray-200 bg-card shadow-md p-6 space-y-6 mt-6">
      <h2 class="text-2xl font-bold text-foreground">Nutrition Distribution</h2>

      <div class="flex justify-center">
        <p-chart type="doughnut" [data]="chartData" style="width: 300px"> </p-chart>
      </div>
    </div>

    <!-- ============================= -->
    <!-- Health Suggestions -->
    <!-- ============================= -->

    <div class="rounded-2xl border border-gray-200 bg-card shadow-md p-6 space-y-6 mt-6">
      <!-- Header -->
      <div class="flex items-center gap-3">
        <div class="bg-green-100 text-green-600 p-3 rounded-xl">
          <i class="pi pi-heart"></i>
        </div>

        <h1 class="text-2xl font-bold text-foreground">Health Suggestions</h1>
      </div>

      <!-- Suggestions Grid -->
      <div class="grid md:grid-cols-3 gap-6">
        <!-- Hydration -->
        <div class="bg-blue-50 rounded-xl p-5 hover:shadow-md transition">
          <div class="flex items-center gap-3 mb-3">
            <div class="bg-blue-100 p-2 rounded-lg text-blue-600">
              <i class="pi pi-sparkles"></i>
            </div>

            <h3 class="font-semibold text-gray-800">Hydration Tips</h3>
          </div>

          <p class="text-gray-600 text-sm leading-relaxed">
            {{ dietData?.extra_suggestions?.hydration_tips }}
          </p>
        </div>

        <!-- Exercise -->
        <div class="bg-orange-50 rounded-xl p-5 hover:shadow-md transition">
          <div class="flex items-center gap-3 mb-3">
            <div class="bg-orange-100 p-2 rounded-lg text-orange-600">
              <i class="pi pi-bolt"></i>
            </div>

            <h3 class="font-semibold text-gray-800">Exercise Recommendations</h3>
          </div>

          <p class="text-gray-600 text-sm leading-relaxed">
            {{ dietData?.extra_suggestions?.exercise_recommendations }}
          </p>
        </div>

        <!-- Additional -->
        <div class="bg-purple-50 rounded-xl p-5 hover:shadow-md transition">
          <div class="flex items-center gap-3 mb-3">
            <div class="bg-purple-100 p-2 rounded-lg text-purple-600">
              <i class="pi pi-info-circle"></i>
            </div>

            <h3 class="font-semibold text-gray-800">Additional Tips</h3>
          </div>

          <p class="text-gray-600 text-sm leading-relaxed">
            {{ dietData?.extra_suggestions?.additional_recommendations }}
          </p>
        </div>
      </div>
    </div>

    <!-- ============================= -->
    <!-- 15 Day Diet Plan -->
    <!-- ============================= -->

    <div class="space-y-4 rounded-2xl border border-gray-200 p-6 shadow-md bg-card mt-6">
      <h1 class="text-2xl font-bold text-foreground">15 Day Diet Plan</h1>

      <p-accordion>
        <p-accordionTab *ngFor="let day of dietData?.['15_days_diet_plan'][0]" [header]="day.day">
          <div>
            <div class="text-xl text-center text-foreground">Date : {{ day.date }}</div>
          </div>
          <div class="space-y-4">
            <div *ngFor="let meal of day.meals" class="border rounded-xl p-4 bg-card">
              <!-- Meal Header -->

              <div class="flex justify-between items-center mb-2">
                <div class="font-semibold text-foreground">
                  {{ meal.name }}
                </div>

                <div class="text-sm text-gray-500">
                  {{ meal.time }}
                </div>
              </div>

              <!-- Meal Items -->
              <div class="text-sm space-y-1">
                <div *ngFor="let food of meal.items">• {{ food.item }} ({{ food.quantity }})</div>

                <div *ngIf="meal.drink">🥤 {{ meal.drink.item }}</div>
              </div>
            </div>
          </div>
        </p-accordionTab>
      </p-accordion>
    </div>
  </div>

  <!-- ============================= -->
  <!-- Empty State -->
  <!-- ============================= -->

  <div *ngIf="!dietData" class="text-center py-16 text-gray-500">
    <i class="pi pi-folder-open text-4xl mb-4"></i>

    <p>No diet summary available</p>
  </div>
</div>
